import { isRejectedWithValue, isFulfilled, Middleware, MiddlewareAPI } from '@reduxjs/toolkit'
import { isDetailedServerError, isFetchError, isPayloadWithAlert, isServerError } from './utils'
import { addAlert } from '../slices/alerts'

import enLang from '../../lang/germany/en'
import deLang from '../../lang/germany/de'

/**
 * Middleware to save 'alert' objects to the Redux store when server errors or custom alerts are received by RTK
 * @param store The Redux store
 */

const notify: Middleware<{}> = (store: MiddlewareAPI) => next => action => {
  const { settings } = store.getState()
  // Get the user's language from the settings state
  const lang = settings.language
  const messages = lang === 'de' ? deLang : enLang

  // Map of known error codes to user friendly messages
  const knownErrorCodes = new Map([
    // Z6S: when there is already a matching serial number
    ['Z6S', { message: messages?.['static.form.server_error_Z6S'], timeout: 5000 }],
    // order_validation_failed: when the Zendesk auto approval fails. Uses 'undefined' message so server message is used
    ['order_validation_failed', { message: undefined, timeout: 12000 }],
  ])

  // Handle custom alerts :: Checks that 'alert' is in the payload schema
  if (isFulfilled(action) && isPayloadWithAlert(action) && typeof action.payload.alert === 'string') {
    store.dispatch(
      addAlert({
        message: action.payload.alert,
        variant: 'success',
        expiresAfter: 5000,
      })
    )

    return next(action)
  }

  // Handle server generated errors :: Checks the object schema matches the server error schema
  if (isRejectedWithValue(action) && (isServerError(action.payload) || isDetailedServerError(action.payload))) {
    const { payload } = action

    // Generic server errors which return an error code and message. Map to more user friendly message if the code is known
    if (payload.data && payload.data?.err_code) {
      const error = {
        message: knownErrorCodes.get(payload.data.err_code)?.message ?? payload.data.message,
        timeout: knownErrorCodes.get(payload.data.err_code)?.timeout ?? 5000,
      }
      store.dispatch(
        addAlert({
          message: error.message,
          variant: 'error',
          expiresAfter: error.timeout,
        })
      )
      return next(action)
    }

    // Detailed server error with multiple field errors
    if (isDetailedServerError(action.payload)) {
      const message = payload.data.errors[0].description

      store.dispatch(
        addAlert({
          message: message.charAt(0).toUpperCase() + message.slice(1),
          variant: 'error',
          expiresAfter: 5000,
        })
      )
    } else {
      store.dispatch(addAlert({ message: payload.data.message, variant: 'error', expiresAfter: 5000 }))
    }
  } else if (isFetchError(action.payload)) {
    store.dispatch(
      addAlert({
        message: 'Your request was blocked by a CORS policy. Please contact your administrator.',
        variant: 'error',
        expiresAfter: 5000,
      })
    )
  }
  return next(action)
}

export default notify
